import { alchemy, chain } from "@/config";
import { Contract, AlchemyProvider, JsonRpcProvider, Provider, InterfaceAbi, BrowserProvider } from "ethers";

// Formatter function type
type FormatterFunction = (value: any) => any;

const abis: Record<string, InterfaceAbi> = {}; // Mapping (abiName => abi)
const formatters: Record<string, Record<string, FormatterFunction | undefined>> = {}; // Mapping (abiName => method => outputFormatterFunction)

const registry: Record<string, Contract> = {}; // Mapping (address => ethers.Contract)
const registryAbiName: Record<string, string> = {}; // Mapping (address => abiName)

export function registerABI(abiName: string, abi: InterfaceAbi): InterfaceAbi {
  abis[abiName] = abi;
  return abis[abiName];
}

export function getABI(abiName: string): InterfaceAbi | undefined {
  return abis[abiName];
}

export function registerFormatter(abiName: string, method: string, formatter: FormatterFunction | undefined): void {
  if (formatters[abiName] === undefined) {
    formatters[abiName] = { [method]: formatter };
  } else {
    formatters[abiName][method] = formatter;
  }
}

export function getFormatter(abiName: string, method: string): FormatterFunction | undefined {
  return formatters[abiName]?.[method];
}

export function registerContract(address: string, abiName: string, rpc?: string): Contract {
  const provider =
    alchemy.network !== undefined
      ? new AlchemyProvider(alchemy.network, alchemy.apiKey)
      : new JsonRpcProvider(rpc || chain.rpc);
  const contract = new Contract(address, abis[abiName], provider);
  registry[address] = contract;
  registryAbiName[address] = abiName;
  return contract;
}

export async function getSignerContract(
  address: string,
  abiName: string,
  provider: BrowserProvider
): Promise<Contract> {
  const signer = await provider.getSigner();
  return new Contract(address, abis[abiName], signer);
}

export function getContract(address: string, abiName: string, rpc?: string): Contract {
  const ret = registry[address];
  if (ret === undefined) return registerContract(address, abiName, rpc);
  else return ret;
}

export function getAbiName(address: string): string | undefined {
  return registryAbiName[address];
}

export function getEncodedCall(address: string, abiName: string, method: string, args: any[], rpc?: string): string {
  const contract = getContract(address, abiName, rpc);
  return contract.interface.encodeFunctionData(method, args);
}

export async function getTxReceiptStatus(txHash: string, provider: Provider): Promise<number | null> {
  const receipt = await provider.getTransactionReceipt(txHash);
  if (receipt) return receipt.status;
  else return null;
}
